import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { doLogout } from '../services/userDataService';
import { API_BASE_URL } from '../config/api';
import Constants from "expo-constants";
import '../styles/custom.css';

const { width, height } = Dimensions.get('window');

export default function VerificationScreen({ navigation }) {
  const codeLength = 6;
  const [code, setCode] = useState(Array(codeLength).fill(''));
  const [email, setEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [cooldownTimer, setCooldownTimer] = useState(null);

  const [isEditable, setIsEditable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error' // 'error' or 'success'
  });

  const handleInputChange = (index, value) => {
    const tmpCode = [...code];
    if( value.length > 1 ){
      value.split('').forEach((char, charIndex) => {
        if( charIndex < codeLength ) {
          tmpCode[charIndex] = char;
        }
      });
      setCode(tmpCode);
    } else if( value.length == 1 ){
      tmpCode[index] = value.toUpperCase();
      setCode(tmpCode);
    } else {
      console.log(value);
    }
  };

  const handleKeyPress = (e) => {
    if( e.key == "Backspace" ) {
      // // find last index of filled code
      // const inputs = document.querySelectorAll("input");
      // const emptyCodeIndex = code.indexOf('');
      // if( emptyCodeIndex > 0 ) {
      //   inputs[emptyCodeIndex - 1].focus();
      // }
      const tmpCode = [...code];
      tmpCode[Math.max(0, code.join('').trim().length-1)] = '';
      setCode(tmpCode);
    }
  }

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error') => {
    setAlertModal({
      visible: true,
      title,
      message,
      type
    });
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false }));
  };

  const verify = async () => {
    console.log('verify called');
    
    setIsEditable(false);
    setIsLoading(true);

    try {
      // Prepare registration data according to updated API specification
      const verificationData = new FormData();
      verificationData.append('user_id', email);
      verificationData.append('verification-code', code.join(''));

      console.log('Sending verification request...');

      const response = await fetch(`${API_BASE_URL}/api/v1/verify`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          "X-Api-Key": Constants.expoConfig?.extra?.API_KEY
        },
        body: verificationData
      });

      const result = await response.json();

      if (result.status === 'success') {
        // Store user data if registration successful
        if (result.data) {
          // Store session data if provided
          if (result.data.auth_token) {
            await AsyncStorage.setItem('auth_token', result.data.auth_token);
          }
        }

        showCustomAlert(
          'Berhasil',
          result.message || 'Verifikasi berhasil.',
          'success'
        );

        // Navigate to login after a short delay
        setTimeout(() => {
          navigation.navigate('MainTabs');
        }, 2000);
      } else {
        showCustomAlert('Error', result.message || 'Verifikasi gagal. Silakan coba lagi.');
      }

    } catch (error) {
      console.error('Verification error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showCustomAlert('Error', 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
      } else {
        showCustomAlert('Error', error.message || 'Verifikasi gagal. Silakan coba lagi.');
      }
    } finally {
      setCode(Array(codeLength).fill(''));
      setIsEditable(true);
      setIsLoading(false);
      document.querySelector("input").focus();
    }
  };

  const handleRequestCode = async () => {
    console.log('handleRequestCode called');
    
    setIsLoading(true);

    try {
      // Prepare registration data according to updated API specification
      const requestData = new FormData();
      requestData.append('user_id', email);

      console.log('Sending request of new code request...');

      const response = await fetch(`${API_BASE_URL}/api/v1/resend-verification`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          "X-Api-Key": Constants.expoConfig?.extra?.API_KEY
        },
        body: requestData
      });

      const result = await response.json();

      if (result.status === 'success') {
        // Store user data if registration successful
        if (result.data) {
          // Store session data if provided
          if (result.data.auth_token) {
            await AsyncStorage.setItem('auth_token', result.data.auth_token);
          }
        }

        setCooldown(180);

        showCustomAlert(
          'Berhasil',
          result.message || 'Request kode verifikasi berhasil.',
          'success'
        );
      } else {
        showCustomAlert('Error', result.message || 'Request kode verifikasi gagal. Silakan coba lagi.');
      }

    } catch (error) {
      console.error('Request error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showCustomAlert('Error', 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
      } else {
        showCustomAlert('Error', error.message || 'Request kode verifikasi gagal. Silakan coba lagi.');
      }
    } finally {
      setCode(Array(codeLength).fill(''));
      setIsLoading(false);
      document.querySelector("input").focus();
    }
  };

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');

      // get user data from storage
      const userDataString = await AsyncStorage.getItem('userData');
      if(!userDataString) {
        throw new Error('Data user tidak ditemukan');
      }
      const userData = JSON.parse(userDataString);
      if(!userData?.user_email) {
        throw new Error('Email user tidak ditemukan');
      }
      
      setEmail(userData?.user_email);
    } catch (error) {
      console.log('Error loading user data:', error);
      doLogout();
    }
  };

  const formatCooldown = () => {
    let m = String( Math.floor( Number( cooldown ) / 60 ) ).padStart(2, '0');
    let s = String( Number( cooldown ) % 60).padStart(2, '0');
    return `${m}:${s}`;
  }

  // Load settings
  useEffect(() => {
    loadUserData();
    document.querySelector('input').focus();
  }, []);

  useEffect(()=>{
    const inputs = document.querySelectorAll("input");
    const nextFocus = inputs[Math.min(code.join('').trim().length, codeLength - 1)];
    if( nextFocus ) nextFocus.focus();
    
    if( code.every(c => c) && email ) {
      verify();
    }
  }, [code]);

  useEffect(()=>{
    if( cooldown > 0 && !cooldownTimer ) {
      setCooldownTimer( setInterval(() => {
        if (cooldown > 0) {
          setCooldown(prevCooldown => prevCooldown - 1);
        } else {
          clearInterval(cooldownTimer);
          setCooldownTimer(null);
        }
      }, 1000) );
    }
  }, [cooldown])

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <Text style={styles.title}>{'Email Belum Terverifikasi'}</Text>
              <Text style={styles.subtitle}>
                {'Cek email yang kamu daftarkan, dan masukkan kode yang kamu terima di sini untuk memverifikasi emailmu.'}
              </Text>
              <Text style={styles.subtitle}>
                Periksa folder <b><i>spam</i></b> atau <b><i>junk</i></b>, jika tidak ada email masuk di kontak masuk utama.
              </Text>
            </View>

            <View style={styles.formSection}>
              <View style={styles.formGroup}>
                <View style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between',
                }}>
                  {code.map((letter, index) => (
                    <View style={isEditable ? styles.inputContainer : styles.inputContainerDisabled} key={index}>
                      <TextInput
                        style={styles.input}
                        placeholder={'*'}
                        placeholderTextColor="#94A3B8"
                        value={code[index]}
                        onChangeText={(value) => handleInputChange(index, value)}
                        onKeyPress={handleKeyPress}
                        autoCapitalize="words"
                        autoCorrect={false}
                        maxLength={index == 0 && code[index] == '' ? null : 1 }
                      />
                    </View>
                  ))}
                </View>
              </View>

              {isLoading ? (
                <ActivityIndicator size="small" color="#1E293B" />
              ) : (
                <View>
                  {cooldown > 0 ? (
                    <View style={styles.loginContainer}>
                      <Text style={styles.loginText}>{`Kamu dapat request ulang kode dalam `}</Text>
                      <Text style={{...styles.loginText, fontWeight: 'bold'}}>{formatCooldown()}</Text>
                    </View>
                  ) : (
                    <View style={styles.loginContainer}>
                      <Text style={styles.loginText}>Belum menerima kode? </Text>
                      <TouchableOpacity onPress={handleRequestCode}>
                        <Text style={styles.loginLink}>Kirim Ulang Kode</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </View>

      {/* Custom Alert Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={alertModal.visible}
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={[styles.modalHeader, alertModal.type === 'success' ? styles.successHeader : styles.errorHeader]}>
              <Text style={styles.modalTitle}>{alertModal.title}</Text>
            </View>
            <View style={styles.modalBody}>
              <Text style={styles.modalMessage}>{alertModal.message}</Text>
            </View>
            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[styles.modalButton, alertModal.type === 'success' ? styles.successButton : styles.errorButton]}
                onPress={hideCustomAlert}
              >
                <Text style={styles.modalButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}


const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    marginTop: 56,
  },
  titleSection: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
    textWrap: 'nowrap',
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  inputContainer: {
    width: '13%',
    maxWidth: '13%',
    // flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 10,
    height: 48,
  },
  inputContainerDisabled: {
    width: '13%',
    maxWidth: '13%',
    // flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 10,
    height: 48,
  },
  input: {
    width: '100%',
    // flex: 1,
    fontSize: 20,
    color: '#1E293B',
    marginVertical: 'auto',
    textAlign: 'center',
    fontFamily: 'Poppins',
  },
  eyeIcon: {
    padding: 4,
  },
  inputIcon: {
    marginLeft: 8,
  },
  uploadContainer: {
    borderWidth: 1,
    borderColor: '#94A3B8',
    borderStyle: 'dashed',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    height: 136,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadContent: {
    alignItems: 'center',
    width: 208,
  },
  uploadText: {
    fontSize: 14,
    color: '#1E293B',
    marginTop: 8,
    fontWeight: '600',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 4,
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 16,
  },
  uploadContainerProcessing: {
    borderColor: '#3B82F6',
    backgroundColor: '#F0F9FF',
  },
  ocrHintText: {
    fontSize: 11,
    color: '#3B82F6',
    marginTop: 6,
    fontFamily: 'Poppins',
    textAlign: 'center',
    fontWeight: '500',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  linkText: {
    color: '#3B82F6',
    fontWeight: '500',
  },
  registerButton: {
    backgroundColor: '#000000',
    borderRadius: 6,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  disabledButton: {
    opacity: 0.7,
  },
  registerButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 400,
    color: '#64748B',
  },
  loginLink: {
    fontSize: 14,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#1E293B',
  },
  // Custom Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 320,
    overflow: 'hidden',
    elevation: 5,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
  },
  modalHeader: {
    padding: 20,
    paddingBottom: 15,
  },
  successHeader: {
    backgroundColor: '#10B981',
  },
  errorHeader: {
    backgroundColor: '#EF4444',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  modalBody: {
    padding: 20,
    paddingTop: 10,
    paddingBottom: 15,
  },
  modalMessage: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 24,
  },
  modalFooter: {
    padding: 20,
    paddingTop: 10,
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  successButton: {
    backgroundColor: '#10B981',
  },
  errorButton: {
    backgroundColor: '#EF4444',
  },
  modalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});